import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { MushakService } from '../../services/mushak.service';
import { IndirectTaxService } from '../../services/indirectTax.service';
import { AuthService } from '../../services/auth.service';
import { Mushak91, Mushak91Line, Mushak91VdsLine, TaxFilingFrequency } from '../../interfaces/indirectTax.interface';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';

const label = (v?: string | null) => (v ? v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase()) : '');

const LinesTable: React.FC<{ title: string; lines: Mushak91Line[]; input?: boolean; testId: string }> = ({ title, lines, input, testId }) => (
  <Paper sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
    <Typography variant="subtitle1">{title}</Typography>
    <Table data-help-section="tax.mushak91.lines" size="small" sx={{ mt: 1 }} data-testid={testId}>
      <TableHead>
        <TableRow>
          <TableCell data-help="tax.mushak91.col.line">Line</TableCell>
          <TableCell data-help="tax.mushak91.col.value" align="right">Value</TableCell><TableCell data-help="tax.mushak91.col.sd" align="right">SD</TableCell>
          <TableCell data-help="tax.mushak91.col.vat" align="right">VAT</TableCell><TableCell data-help="tax.mushak91.col.other" align="right">Other</TableCell>
          {input && <TableCell data-help="tax.mushak91.col.creditable" align="right">Creditable</TableCell>}
          <TableCell data-help="tax.mushak91.col.note">Note</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map(l => {
          const total = l.key === 'TOTAL';
          const cell = (v: number) => (total ? <strong>{formatMoney(v)}</strong> : formatMoney(v));
          return (
            <TableRow key={l.key}>
              <TableCell>{total ? <strong>{l.label}</strong> : l.label}</TableCell>
              <TableCell align="right">{cell(l.value)}</TableCell>
              <TableCell align="right">{cell(l.sd)}</TableCell>
              <TableCell align="right">{cell(l.vat)}</TableCell>
              <TableCell align="right">{cell(l.other)}</TableCell>
              {input && <TableCell align="right">{cell(l.creditableTax ?? 0)}</TableCell>}
              <TableCell>{l.note}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Paper>
);

// ---- B4-A: VDS adjustment section ----
const VdsTable: React.FC<{ title: string; lines: Mushak91VdsLine[]; total: number; testId: string; dateLabel?: string }> = ({ title, lines, total, testId, dateLabel = 'Date' }) => (
  <Box sx={{ mt: 1, overflowX: 'auto' }}>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
    <Table data-help-section="tax.mushak91.vdsTable" size="small" data-testid={testId}>
      <TableHead>
        <TableRow>
          <TableCell data-help="tax.mushak91.vds.col.reference">Reference</TableCell><TableCell data-help="tax.mushak91.vds.col.date">{dateLabel}</TableCell><TableCell data-help="tax.mushak91.vds.col.party">Party</TableCell><TableCell data-help="tax.mushak91.vds.col.bin">BIN</TableCell>
          <TableCell data-help="tax.mushak91.vds.col.certificate">Certificate</TableCell><TableCell data-help="tax.mushak91.vds.col.challan">Challan</TableCell><TableCell data-help="tax.mushak91.vds.col.amount" align="right">VDS</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.length === 0 && <TableRow><TableCell colSpan={7}>None in this period.</TableCell></TableRow>}
        {lines.map((l, i) => (
          <TableRow key={`${l.certificateNumber}-${i}`}>
            <TableCell>{l.reference}</TableCell><TableCell>{formatDate(l.date)}</TableCell><TableCell>{l.partyName}</TableCell>
            <TableCell>{l.partyBin}</TableCell>
            <TableCell>{l.deductible ? l.certificateNumber : <Typography component="span" variant="body2" color="error">NOT DEDUCTED - no certificate</Typography>}</TableCell>
            <TableCell>{l.challanNumber || '-'}</TableCell><TableCell align="right">{formatMoney(l.amount)}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={6}><strong>Total</strong></TableCell>
          <TableCell align="right" data-testid={`${testId}-total`}><strong>{formatMoney(total)}</strong></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </Box>
);
// ---- end B4-A ----

const Mushak91Page: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() as string;

  const [frequency, setFrequency] = useState<TaxFilingFrequency>('MONTHLY');
  // ?period=yyyy-MM (a reminder link): the first month of the return period to open.
  const linkPeriod = useSearchParams()[0].get('period');
  const startMonth = linkPeriod && /^\d{4}-\d{2}$/.test(linkPeriod) ? linkPeriod : toLocalIsoDate().slice(0, 7);
  const [month, setMonth] = useState(startMonth); // yyyy-MM
  const [year, setYear] = useState(Number(startMonth.slice(0, 4)));
  const [quarter, setQuarter] = useState(Math.floor((Number(startMonth.slice(5, 7)) - 1) / 3) + 1);
  const [data, setData] = useState<Mushak91 | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const periodDate = frequency === 'QUARTERLY'
    ? `${year}-${String((quarter - 1) * 3 + 1).padStart(2, '0')}-01`
    : `${month}-01`;

  useEffect(() => {
    IndirectTaxService.getProfile(institutionId)
      .then(p => { if (p?.filingFrequency) setFrequency(p.filingFrequency); })
      .catch(() => undefined)
      .finally(() => setProfileLoaded(true));
  }, [institutionId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await MushakService.get91(institutionId, periodDate));
    } catch (e) {
      setData(null);
      toast.showApiError(e, 'Failed to load Mushak 9.1 working paper');
    } finally {
      setLoading(false);
    }
  }, [institutionId, periodDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (profileLoaded) load(); }, [profileLoaded, load]);

  const doExport = async (kind: 'pdf' | 'xlsx') => {
    try {
      setExporting(true);
      const blob = kind === 'pdf'
        ? await MushakService.download91Pdf(institutionId, periodDate)
        : await MushakService.download91Xlsx(institutionId, periodDate);
      saveBlob(blob, `mushak-9-1-working-${data?.periodStart ?? periodDate}.${kind}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to export working paper');
    } finally {
      setExporting(false);
    }
  };

  const s = data?.summary;
  const rows: Array<[string, number]> = s ? [
    ['Output VAT', s.outputVat], ['Output supplementary duty', s.outputSd], ['Output other tax', s.outputOther],
    ['Creditable input VAT', s.creditableVat], ['Input supplementary duty (information only, never offsets SD)', s.creditableSd],
    ['Input other tax (information only, never offsets)', s.creditableOther],
    ['Non-recoverable input tax (booked as cost, not claimed)', s.nonRecoverableTax],
    ['Net VAT (negative = refundable credit carried forward)', s.netVat], ['Supplementary duty payable (in full)', s.netSd], ['Other tax payable (in full)', s.netOther],
  ] : [];

  return (
    <Box data-help-page="tax.mushak91.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Mushak 9.1 - Return Working Paper</Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Working paper - file the return on the NBR e-VAT portal. Figures come from the tax ledger and are not a filed return.
      </Alert>

      <Box data-help-section="tax.mushak91.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        {frequency === 'QUARTERLY' ? (
          <>
            <Box sx={{ flex: '1 1 120px', maxWidth: 160 }}>
              <TextField data-help="tax.mushak91.year" fullWidth size="small" type="number" label="Year" value={year}
                onChange={e => setYear(Number(e.target.value) || year)} />
            </Box>
            <Box sx={{ flex: '1 1 140px', maxWidth: 200 }}>
              <TextField data-help="tax.mushak91.quarter" select fullWidth size="small" label="Quarter" value={quarter} onChange={e => setQuarter(Number(e.target.value))}>
                {[1, 2, 3, 4].map(q => <MenuItem key={q} value={q}>Q{q}</MenuItem>)}
              </TextField>
            </Box>
          </>
        ) : (
          <Box sx={{ flex: '1 1 180px', maxWidth: 240 }}>
            <TextField data-help="tax.mushak91.period" fullWidth size="small" type="month" label="Period" value={month} InputLabelProps={{ shrink: true }}
              onChange={e => e.target.value && setMonth(e.target.value)} />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-help="tax.mushak91.pdf" variant="outlined" onClick={() => doExport('pdf')} disabled={exporting || loading || !data}>Download PDF</Button>
          <Button data-help="tax.mushak91.xlsx" variant="outlined" onClick={() => doExport('xlsx')} disabled={exporting || loading || !data}>Export XLSX</Button>
        </Box>
      </Box>

      {loading && <CircularProgress />}

      {!loading && data && s && (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {data.institutionName} | BIN {data.bin || '(not set)'} | {formatDate(data.periodStart)} to {formatDate(data.periodEnd)}
            {' '}({label(data.filingFrequency)}, {label(data.regime)})
          </Typography>

          <LinesTable title="A. Sales (output)" lines={data.sales} testId="mushak91-sales" />
          <LinesTable title="B. Purchases (input)" lines={data.purchases} input testId="mushak91-purchases" />

          <Paper sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
            <Typography variant="subtitle1">C. Net position</Typography>
            <Table data-help-section="tax.mushak91.summary" size="small" sx={{ mt: 1 }}>
              <TableBody>
                {rows.map(([k, v]) => (
                  <TableRow key={k}><TableCell>{k}</TableCell><TableCell align="right">{formatMoney(v)}</TableCell></TableRow>
                ))}
                <TableRow>
                  <TableCell><strong>{s.netPayable < 0 ? 'Net credit for the period' : 'Net payable for the period'}</strong></TableCell>
                  <TableCell align="right" data-testid="mushak91-net"><strong>{formatMoney(Math.abs(s.netPayable))}</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Credit brought forward from the previous settlement</TableCell>
                  <TableCell align="right" data-testid="mushak91-bf">{formatMoney(s.broughtForwardCredit)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Net after brought-forward credit (working figure, not posted)</TableCell>
                  <TableCell align="right">{formatMoney(s.netAfterBroughtForward)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          {/* ---- B4-A: section D, VDS adjustments ---- */}
          {data.vds && (
            <Paper data-help-section="tax.mushak91.vds" sx={{ p: 2, mb: 2, overflowX: 'auto' }} data-testid="mushak91-vds">
              <Typography variant="subtitle1">D. VDS adjustments (working only, not posted)</Typography>
              <VdsTable title={`VAT deducted at source by customers (decreasing adjustment, Part 6) - ${data.vds.customerNoteRef ?? 'note no. not set'}`}
                lines={data.vds.customerDeducted} total={data.vds.customerTotal} testId="mushak91-vds-customers" />
              <VdsTable title={`VAT we deducted from suppliers and deposited (increasing adjustment, Part 5) - ${data.vds.supplierNoteRef ?? 'note no. not set'}`}
                lines={data.vds.supplierDeducted} total={data.vds.supplierTotal} testId="mushak91-vds-suppliers" dateLabel="Payment date" />
              <Typography variant="body2" sx={{ mt: 1 }}>
                VAT side after brought-forward credit and customer VDS (working): <strong data-testid="mushak91-net-after-vds">{formatMoney(data.vds.vatSideAfterVds)}</strong>
                {data.vds.vatCreditAfterVds > 0 && <> - VAT credit carried forward <strong data-testid="mushak91-vds-credit">{formatMoney(data.vds.vatCreditAfterVds)}</strong></>}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Payable after VDS (VAT side floored at zero + SD + other): <strong data-testid="mushak91-payable-after-vds">{formatMoney(data.vds.payableAfterVds)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Mushak 6.10 attached: {data.vds.highValuePurchaseDocuments} purchase and {data.vds.highValueSalesDocuments} sales
                document(s) above BDT {formatMoney(data.vds.highValueThreshold)}.
              </Typography>
            </Paper>
          )}
          {/* ---- end B4-A ---- */}

          <Paper data-help-section="tax.mushak91.settlement" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ flex: '1 1 200px' }}>Settlement status</Typography>
              {data.settlement
                ? <Chip size="small" color={data.settlement.status === 'SETTLED' ? 'success' : 'warning'} label={label(data.settlement.status)} />
                : <Chip size="small" label="Not settled" />}
              {data.settlement?.changedSinceSettlement && <Chip size="small" color="warning" label="Changed since settlement" />}
            </Box>
            {data.settlement && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Net {formatMoney(data.settlement.netAmount)} | Paid {formatMoney(data.settlement.paidAmount)} | Outstanding {formatMoney(data.settlement.outstandingAmount)}
              </Typography>
            )}
          </Paper>

          {data.notes.map(n => <Typography key={n} variant="caption" color="text.secondary" display="block">{n}</Typography>)}
        </>
      )}
    </Box>
  );
};

export default Mushak91Page;
